import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../AuthContext';

const FEEDBACK_TYPES = [
  "Can't find a car",
  "Don't understand Mestimate",
  'Wrong price',
  'Bad image',
  'Wrong vehicle info',
  'Site bug',
  'Other',
];

export default function FeedbackWidget() {
  const { token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState(FEEDBACK_TYPES[0]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeModal = () => {
    if (isSubmitting) return;
    setIsOpen(false);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!message.trim()) {
      setError('Please enter a message.');
      return;
    }

    setIsSubmitting(true);

    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE}/feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          type,
          message: message.trim(),
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
        }),
      });

      if (!response.ok) {
        throw new Error('Feedback request failed');
      }

      setMessage('');
      setType(FEEDBACK_TYPES[0]);
      setIsOpen(false);
      toast.success('Thanks — your feedback helps us improve Meshum.');
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Unable to send feedback', err);
      }
      setError('Unable to send feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="feedback-floating-button"
        onClick={() => setIsOpen(true)}
        aria-label="Open feedback form"
      >
        <span className="feedback-button-icon" aria-hidden="true">?</span>
        <span className="feedback-button-label">Feedback</span>
      </button>

      {isOpen && (
        <div
          className="feedback-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
        >
          <section
            className="feedback-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-title"
          >
            <div className="feedback-modal-header">
              <div>
                <p className="feedback-brand">Meshum</p>
                <h2 id="feedback-title">Help us improve Meshum</h2>
              </div>
              <button
                type="button"
                className="feedback-close-button"
                onClick={closeModal}
                aria-label="Close feedback form"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="feedback-form">
              <label className="feedback-field">
                <span>Type</span>
                <span className="feedback-select-wrap">
                  <select
                    className="feedback-type-select"
                    value={type}
                    onChange={(event) => setType(event.target.value)}
                    disabled={isSubmitting}
                  >
                    {FEEDBACK_TYPES.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </span>
              </label>

              <label className="feedback-field">
                <span>Message</span>
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="Tell us what happened..."
                  required
                  maxLength={5000}
                  disabled={isSubmitting}
                />
              </label>

              {error && <div className="feedback-error">{error}</div>}

              <div className="feedback-actions">
                <button
                  type="button"
                  className="feedback-secondary-button"
                  onClick={closeModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="feedback-submit-button"
                  disabled={isSubmitting || !message.trim()}
                >
                  {isSubmitting ? 'Sending...' : 'Send feedback'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </>
  );
}

